import React, { useContext, useEffect, useRef } from "react";

import { Helmet } from "react-helmet-async";

import { TYPE_FAVOURITE_PRODUCTS } from "../../contexts/Favourites";
import AddProductPrice from "../../product/components/AddProductPrice";
import { LinkHelper } from "../../helpers";
import {
  AddToCartButton,
  ReferListingButton,
  Money,
  PartExchangeButton,
  Separator,
  Star,
  UserCard,
  EnquireButton,
} from "../../components";
import { Specification } from "./index.jsx";
import ShareToBroadcast from "./ShareToBroadcast";
import AutoHideScrollContainer from "../../components/AutoHideScrollContainer";
import { Button, ButtonGroup, Icon, IconButton } from "../../components/ui";
import { SettingsContext } from "../../contexts";
import { getFileUrl } from "../../core/config/api.js";

const SCROLLABLE_LOWER_LIMIT_WIDTH = 1200; // px
const SCROLLABLE_CONTAINER_HEIGHT = 620; // px — fallback only, when the details column can't be measured
const HERO_LAYOUT_BREAKPOINT = 1024; // px — matches $bp-product-stack in rc-product-view.scss
const MIN_SCROLL_HEIGHT = 240; // px

// Height left for the scroll area inside a details column of `containerHeight`,
// after its non-scrolling parts (the top block, everything below the scroll
// container, and the column's own vertical padding).
const availableScrollHeight = (detailsRoot, topEl, containerHeight) => {
  let belowScrollHeight = 0;
  // topEl -> AutoHideScrollContainer -> separators / info / bottom border
  let node = topEl?.nextElementSibling?.nextElementSibling;

  while (node) {
    belowScrollHeight += node.offsetHeight;
    node = node.nextElementSibling;
  }

  const styles = window.getComputedStyle(detailsRoot);
  const verticalPadding =
    parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);

  return (
    containerHeight -
    (topEl?.offsetHeight || 0) -
    belowScrollHeight -
    verticalPadding
  );
};

const selectLabel = (requestType) => {
  switch (requestType) {
    case "sell":
      return "Sell";
    case "order":
      return "Order";
    default:
      return "Select";
  }
};

const ListingDetails = ({
  user,
  listing,
  onSelectProduct,
  onClose,
  isProductPreview = false,
  hideLowerSeparator = false,
  requestType,
  heroLayout = false,
}) => {
  const { settings, isShopify } = useContext(SettingsContext);
  const topRef = useRef(null);
  const scrollableRef = useRef(null);

  useEffect(() => {
    const syncScrollHeight = () => {
      if (!scrollableRef.current) {
        return;
      }

      const detailsRoot = topRef.current?.closest(".product-details");

      // Preview/modal hero layout: match scroll area to gallery height
      if (heroLayout && window.innerWidth >= HERO_LAYOUT_BREAKPOINT) {
        const gallery = detailsRoot
          ?.closest(".rc-product-hero")
          ?.querySelector(".rc-product-hero__gallery");

        if (detailsRoot && gallery) {
          const available = availableScrollHeight(
            detailsRoot,
            topRef.current,
            gallery.offsetHeight
          );

          scrollableRef.current.style.height = `${Math.max(
            MIN_SCROLL_HEIGHT,
            available
          )}px`;
          return;
        }
      }

      // DAO product-page scroll logic
      if (window.innerWidth < SCROLLABLE_LOWER_LIMIT_WIDTH) {
        scrollableRef.current.style.height = "fit-content";
        return;
      }

      // Product page (lg+): the hero row is exactly one screen tall (gallery.css
      // sizes it to the viewport), so the scroll area takes whatever the details
      // column has left — the card and the image always end at the same fold.
      if (detailsRoot) {
        const available = availableScrollHeight(
          detailsRoot,
          topRef.current,
          detailsRoot.clientHeight
        );

        scrollableRef.current.style.height = `${Math.max(
          MIN_SCROLL_HEIGHT,
          available
        )}px`;
        return;
      }

      const scrollableHeight =
        SCROLLABLE_CONTAINER_HEIGHT - (topRef.current?.clientHeight || 0);

      scrollableRef.current.style.height = `${scrollableHeight}px`;
    };

    syncScrollHeight();
    window.addEventListener("resize", syncScrollHeight);

    return () => {
      window.removeEventListener("resize", syncScrollHeight);
    };
  }, [heroLayout, hideLowerSeparator, isProductPreview]);

  const product = listing.product;
  const signedOut = !user;

  // Both relations are eager-loaded in practice, but a catalog row missing
  // either one should not blank the page — same lesson as og:image below.
  const title = [product?.brand?.name, product?.model?.name].filter(Boolean).join(" ");
  const url = LinkHelper.listingLink(listing);

  const mailSubject = `Query regarding ${settings.name} listing of ` + title;
  const mailBody = "Hi,\n\nwith regards to " + url + " ...";
  const whatsAppMessage = "Hi, I have a question regarding " + url;
  const whatsAppEnquireMessage =
    "Hi, I am interested in this listing and would like to know its price. " +
    url;

  return (
    <div className={`overview details product-details${signedOut ? " product-details--readonly" : ""}`}>
      <div className="product-details-top" ref={topRef}>
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={listing.description} />
          <meta property="og:title" content={title} />
          <meta property="og:url" content={url} />
          <meta property="og:description" content={listing.description} />
          {/* Guarded: a product with no images made this throw and the preview
              page rendered NOTHING at all. A missing social-preview image is not
              worth a blank page — omit the tag instead. */}
          {product.images?.[0]?.id && (
            <meta
              property="og:image"
              content={LinkHelper.link(getFileUrl(product.images[0].id))}
            />
          )}
          <meta property="og:type" content="article" />
        </Helmet>

        {isProductPreview ? (
          <div className="rc-product-details__header-bar">
            <p className="subtitle">{product.reference_number}</p>
            <div className="rc-product-details__header-actions">
              <Star
                item={listing}
                type={TYPE_FAVOURITE_PRODUCTS}
                className="small"
              />
              {onClose && (
                <IconButton
                  variant="ghost"
                  size="sm"
                  label="Close"
                  onClick={onClose}
                  data-btn="product-close"
                >
                  <Icon name="X" size="sm" />
                </IconButton>
              )}
            </div>
          </div>
        ) : (
          <div className="d-flex justify-content-between">
            <p className="subtitle">{product.reference_number}</p>
            {!signedOut && <Star item={listing} className="small" />}
          </div>
        )}

        <h1>
          <span className="no-wrap">
            {product.brand.name}
            &nbsp;
          </span>
          <span className="no-wrap">{product.model.name}</span>
        </h1>

        {!isProductPreview && (
          <div className="d-flex justify-content-between">
            <div className="price">
              <Money amount={listing.price} currency={listing.currency} zero="PRICE ON REQUEST" />
            </div>

            {listing.retail_price > 0 && (
              <small className="market-price">
                Market price: <Money amount={listing.retail_price} currency={listing.currency} />
              </small>
            )}
          </div>
        )}

        <Separator noMargin />
      </div>
      <AutoHideScrollContainer
        ref={scrollableRef}
        className={heroLayout ? "rc-product-details-scroll" : undefined}
        persistentScrollbar={heroLayout}
      >
        <div>
          {/* Signed out = a client reading the page: details only, no actions. */}
          {!signedOut && (
            <div className="buttons-row product-details__icon-actions">
              {/* CORRECT use of `is_dao_admin` (= `is_admin && !is_merchant`), and
                  note it WIDENS rather than restricts: the WhatsApp broadcast
                  button shows on the normal product page for everyone, and
                  additionally inside the internal product-preview modal for
                  non-merchant admins. The shared message is plain listing specs +
                  price (listings/helpers.js buildListingBroadcastMessage) with no
                  RC-internal or cross-tenant data, and the WhatsApp number is
                  already per-store. Tightening this would take a share button away
                  from tenant admins for no security gain. */}
              {(!isProductPreview || user?.is_dao_admin) && !isShopify && (
                <ShareToBroadcast listing={listing} />
              )}

              {!isShopify && (
                <ReferListingButton
                  listing={listing}
                  isMember={!!user}
                  isProductPreview={isProductPreview}
                />
              )}
            </div>
          )}

          {/* DAO display order: CTAs → enquire/select → seller → description → specs */}
          {!signedOut && !isProductPreview && !!listing.price && (
            <ButtonGroup equal className="product-details-cta-row">
              <AddToCartButton listing={listing} />
              <PartExchangeButton listing={listing} />
            </ButtonGroup>
          )}

          {!signedOut && !isProductPreview && !listing.price && listing.price === 0 && (
            <ButtonGroup align="stretch">
              <EnquireButton whatsAppEnquireMessage={whatsAppEnquireMessage} />
            </ButtonGroup>
          )}

          {isProductPreview && onSelectProduct && (
            <Button
              fullWidth
              onClick={() => onSelectProduct(listing)}
              data-btn="product-select"
            >
              {selectLabel(requestType)}
            </Button>
          )}

          {/* "Add Price" writes a supplier price observation to product_prices.
              That table IS tenant-scoped as of the 2026-08 release
              (ProductPrice uses ScopedByUserStore and the column is backfilled),
              so a store admin's observation is stamped with THEIR store and
              reads are fail-closed — it cannot reach DAO's price history.
              `is_dao_admin` here is therefore acceptable: it means
              `is_admin && !is_merchant`, i.e. the internal request desk rather
              than the merchant portal. Left as-is deliberately — tightening it
              would remove a working capability from tenant admins for no
              security gain. Whether merchants SHOULD contribute price
              observations is a product question, not a leak. */}
          {isProductPreview &&
            (user?.add_price_enabled || user?.is_dao_admin) && (
              <AddProductPrice
                productId={listing?.product?.id}
                product={listing?.product}
              />
            )}

          {!signedOut && listing?.user && !isShopify && (
            <>
              <UserCard user={listing.user} {...{ mailBody, mailSubject, whatsAppMessage }} />
              <Separator
                noMargin
                className="product-details__seller-divider"
              />
            </>
          )}

          {/* Only render when there's copy. This <p> carries `margin: 32px 0`, so
              an empty one silently added ~64px of dead space — which is what made
              the gap between Add Price and Edit far larger than the one above it. */}
          {listing.description ? (
            <p className="description">{listing.description}</p>
          ) : null}

          <Specification
            user={user}
            listing={listing}
            isProductPreview={isProductPreview}
          />
        </div>
      </AutoHideScrollContainer>
      {!hideLowerSeparator && <Separator noMargin />}

    </div>
  );
};

export default ListingDetails;
